import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { waLink, WA_MSG } from "./wa";

const Wrap = styled.section`
  padding: 5rem 0 6rem;
  background: var(--bg-2);
  border-top: 1px solid var(--rule);
`;

const Inner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (min-width: 768px) { padding: 0 2rem; }
`;

const Head = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--mono);
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 1.25rem;
  font-weight: 500;
`;

const Title = styled.h2`
  font-family: var(--sans);
  font-weight: 700;
  font-size: clamp(2.1rem, 4.6vw, 3.6rem);
  line-height: 1.0;
  letter-spacing: -0.032em;
  margin: 0;
  color: var(--ink);

  .accent {
    font-family: var(--serif);
    font-weight: 400;
    font-style: italic;
    letter-spacing: -0.012em;
    color: var(--accent);
  }
`;

const Sub = styled.p`
  font-family: var(--sans);
  font-size: 1rem;
  line-height: 1.55;
  color: var(--ink-2);
  margin: 1.1rem auto 0;
  max-width: 50ch;
`;

const Item = styled.div`
  background: var(--bg);
  border: 1px solid var(--rule);
  border-radius: 14px;
  margin: 0.6rem 0;
  overflow: hidden;
  transition: border-color 220ms ease;

  &:hover { border-color: var(--rule-2); }
`;

const QButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: transparent;
  border: none;
  padding: 1.1rem 1.4rem;
  font-family: var(--sans);
  font-size: 1rem;
  font-weight: 500;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
  letter-spacing: -0.005em;
  line-height: 1.4;

  svg.chev {
    width: 16px;
    height: 16px;
    color: var(--ink-3);
    flex-shrink: 0;
    transition: transform 280ms cubic-bezier(0.6, 0, 0.4, 1), color 200ms ease;
    transform: ${(p) => (p.$open ? "rotate(180deg)" : "rotate(0deg)")};
  }

  &:hover svg.chev { color: var(--ink); }
`;

const Answer = styled(motion.div)`
  overflow: hidden;
`;

const AInner = styled.div`
  padding: 0 1.4rem 1.3rem;
  font-family: var(--sans);
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--ink-2);

  a {
    color: var(--accent);
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
  }

  strong { font-weight: 600; color: var(--ink); }
`;

const Foot = styled.div`
  margin-top: 3rem;
  text-align: center;
  font-family: var(--sans);
  font-size: 1rem;
  color: var(--ink-2);

  a {
    color: var(--ink);
    border-bottom: 1px solid var(--ink);
    text-decoration: none;
    padding-bottom: 1px;
    transition: color 200ms ease, border-color 200ms ease;

    &:hover {
      color: var(--accent);
      border-color: var(--accent);
    }
  }
`;

const FAQS = [
  {
    q: "¿Cómo puedo comenzar y cuál es el costo?",
    a: (
      <>
        Empezamos con una breve reunión para revisar los ajustes iniciales en
        WhatsApp API: tu perfil de negocio en Meta y la cantidad de
        conversaciones a automatizar. Los precios van según la cantidad de
        interacciones que gestionamos con nuestro SaaS.
        <br /><br />
        <a href={waLink(WA_MSG.costo)} target="_blank" rel="noreferrer">Hablemos por WhatsApp →</a>
      </>
    ),
  },
  {
    q: "¿En qué consisten los ajustes iniciales?",
    a: (
      <>
        Para iniciar la integración consideramos:
        <br /><br />
        1. Que tu cuenta de Administrador Comercial de Facebook esté verificada.
        <br />
        2. Tener un número no asociado con una cuenta de WhatsApp.
        <br /><br />
        Después te ayudamos a verificar el número y a solicitar la aprobación
        de las plantillas de mensajes de tu e-commerce.
      </>
    ),
  },
  {
    q: "¿Facilitan asistencia técnica durante la implementación?",
    a: (
      <>
        Sí. Acompañamos personalmente toda la experiencia con WhatsApp API,
        tanto en lo técnico como en el <strong>copywriting</strong> de tus
        conversaciones.
      </>
    ),
  },
  {
    q: "¿Cuentan con plugins para CMS?",
    a: (
      <>
        Tenemos integración para <strong>WordPress (WooCommerce)</strong>,
        <strong> PrestaShop</strong>, <strong>Shopify</strong> y{" "}
        <strong>Jumpseller</strong> vía API.
        <br /><br />
        También podemos desarrollar una integración nueva — escríbenos a{" "}
        <a href="mailto:hola@clienterecurrente.cl">hola@clienterecurrente.cl</a>.
      </>
    ),
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <Item>
      <QButton $open={open} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span>{q}</span>
        <svg className="chev" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </QButton>
      <AnimatePresence initial={false}>
        {open && (
          <Answer
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
          >
            <AInner>{a}</AInner>
          </Answer>
        )}
      </AnimatePresence>
    </Item>
  );
}

export default function Faqs() {
  return (
    <Wrap id="faq">
      <Inner>
        <Head>
          <Title>Preguntas frecuentes</Title>
          <Sub>
            Las consultas más frecuentes — y si no está aquí, te respondemos por
            WhatsApp.
          </Sub>
        </Head>

        {FAQS.map((f, i) => (
          <FaqItem key={i} q={f.q} a={f.a} />
        ))}

        <Foot>
          ¿Tu pregunta no está aquí?{" "}
          <a href={waLink(WA_MSG.duda)} target="_blank" rel="noreferrer">
            Escríbenos por WhatsApp →
          </a>
        </Foot>
      </Inner>
    </Wrap>
  );
}
